import { router } from "../router";
import { store } from "../store";
import { i18n } from "../boot/i18n";
import DataStructure, { sanitize } from "./DataStructure";
import { deepFreeze, timestampToDate } from "../util/misc";

// Import shared notification types configuration from functions directory
// This ensures frontend and backend use the same notification type definitions
import notificationTypesConfig from "src/config/notification-types.js";

export const NOTIFICATION_FIELDS = {
  type: "",
  title: "",
  body: "",
  item: "",
  url: "",
  urgency: "normal",
  done: false,
  seen: false,
  timestamp: null,
};
deepFreeze(NOTIFICATION_FIELDS);

export const NOTIFICATION_TYPES = notificationTypesConfig;
deepFreeze(NOTIFICATION_TYPES);

// Whether `route` is on the page a non-hash notification `url` names. A query
// in the url (a task's `/projects/p1?tab=tasks`) must be carried by the route
// as well; `route.path` never includes it, so comparing the whole url against
// the path never matched. Other params on the route (`messageID`) do not
// matter.
function isOnPage(url, route) {
  if (!route || !route.path) {
    return false;
  }
  const [path, search = ""] = url.split("?");
  if (path !== route.path) {
    return false;
  }
  const query = route.query || {};
  for (const [key, value] of new URLSearchParams(search)) {
    if (query[key] !== value) {
      return false;
    }
  }
  return true;
}

export default class Notification extends DataStructure {
  constructor(id, data = {}, isReminder = false) {
    data = sanitize(data, NOTIFICATION_FIELDS);

    // Convert to Date
    data.timestamp = timestampToDate(data.timestamp);

    super(data);

    this.id = id || null;
    this.isReminder = isReminder;
  }

  // Database

  get path() {
    return Notification.getPath(this.id, this.isReminder);
  }

  static getPath(id, reminders = false) {
    let path = `users/${store.state.main.user.id}/${
      reminders ? "reminders" : "notifications"
    }`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(notification) {
    return notification.data;
  }

  static fromFirestore(snapshot, options) {
    const isReminder = snapshot.ref.parent.id === "reminders";
    const data = snapshot.data(options);
    return new Notification(snapshot.id, data, isReminder);
  }

  // Routing

  // Normalize old-format URLs where / was before # (e.g. /issues/#id/view)
  // to the correct format where / is after # (e.g. /issues#/id/view)
  get normalizedUrl() {
    return this.url.replace("/#", "#/");
  }

  isNavigatingTo(fromRoute, toRoute = router.currentRoute.value) {
    const url = this.normalizedUrl;
    if (!url) {
      return false;
    } else if (url[0] === "#" && url === toRoute.hash) {
      return !(toRoute && fromRoute && toRoute.hash === fromRoute.hash);
    } else if (url.includes("#") && url === toRoute.fullPath) {
      return !(toRoute && fromRoute && toRoute.fullPath === fromRoute.fullPath);
    } else if (!url.includes("#") && isOnPage(url, toRoute)) {
      return !isOnPage(url, fromRoute);
    }
  }

  isNavigatingFrom(fromRoute, toRoute) {
    const url = this.normalizedUrl;
    if (!url || !fromRoute) {
      return false;
    } else if (url[0] === "#" && url === fromRoute.hash) {
      return !(toRoute && fromRoute && toRoute.hash === fromRoute.hash);
    } else if (url.includes("#") && url === fromRoute.fullPath) {
      return !(toRoute && fromRoute && toRoute.fullPath === fromRoute.fullPath);
    } else if (!url.includes("#") && isOnPage(url, fromRoute)) {
      return !isOnPage(url, toRoute);
    }
  }

  // Formatting

  get type() {
    return NOTIFICATION_TYPES[this.data.type];
  }

  get iconColor() {
    return this.data.urgency === "high" ? "negative" : "";
  }

  // The project the notification points into, read from its url
  // (`#/projects/0123/sales/A/…`, `/projects/0123/…`); null when it points
  // elsewhere, or nowhere. A task made from it goes on this project.
  get projectID() {
    const match = this.normalizedUrl.match(/(?:^|[#/])projects\/([^/?#]+)/);
    return match ? match[1] : null;
  }

  // The message a "new message" notification is about. A task made from
  // such a notification is made from the message itself.
  get messageID() {
    return this.data.type === "message" ? this.data.item || null : null;
  }

  // The dialog that makes a task of it, for an employee, when it points
  // into a project (null otherwise). Its menu and the bar shown on the page
  // it points to both offer it.
  get taskRoute() {
    if (!this.projectID || this.isReminder || !store.getters.isEmployee()) {
      return null;
    }
    return {
      name: "task-new-from-notification",
      params: { projectID: this.projectID, notificationID: this.id },
    };
  }

  // What it says, in one line, as a task made from it shows it
  get text() {
    return [i18n.global.t(this.data.title), this.data.body]
      .filter(Boolean)
      .join(" – ");
  }
}

// Opens a notification's url: a dialog over the current page for a hash url,
// else the page itself. A component passes its own `$router`.
export function openUrl(url, appRouter = router) {
  if (url[0] === "#") {
    location.hash = url;
  } else {
    appRouter.push(url);
  }
}
